import { type HTMLAttributes } from 'react';
import { cn } from './cn';

export type BadgeTone =
  | 'neutral'
  | 'accent'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  /** Renders a small leading status dot. */
  dot?: boolean;
}

const TONES: Record<BadgeTone, string> = {
  neutral: 'bg-surface-3 text-zinc-300 border-surface-border',
  accent: 'bg-accent-soft/60 text-accent-fg border-accent/40',
  success: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  warning: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  danger: 'bg-red-500/10 text-red-300 border-red-500/30',
  info: 'bg-sky-500/10 text-sky-300 border-sky-500/30',
};

const DOT_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-zinc-400',
  accent: 'bg-accent',
  success: 'bg-emerald-400',
  warning: 'bg-amber-400',
  danger: 'bg-red-400',
  info: 'bg-sky-400',
};

/** Compact status/label pill. */
export function Badge({
  tone = 'neutral',
  dot,
  className,
  children,
  ...rest
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
        TONES[tone],
        className,
      )}
      {...rest}
    >
      {dot && (
        <span
          className={cn('h-1.5 w-1.5 rounded-full', DOT_TONES[tone])}
          aria-hidden="true"
        />
      )}
      {children}
    </span>
  );
}
